import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/mock';
import { instagramAPI } from '../services/api';
import { Footer } from '../components/Footer';
import { SponsorBanner } from '../components/SponsorBanner';

export const Home = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const [instagramPhotos, setInstagramPhotos] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const heroImages = [
    'https://customer-assets.emergentagent.com/job_ocicat-cats/artifacts/5gpee1zy_Ocicat_0731_NOVA.jpg',
    'https://customer-assets.emergentagent.com/job_pedigree-cats/artifacts/xgjg1urj_ocicat-geracao.JPG',
    'https://customer-assets.emergentagent.com/job_pedigree-cats/artifacts/jazhsf7g_clara-creme.JPEG',
    'https://customer-assets.emergentagent.com/job_ocicat-cats/artifacts/mpothu4h_DSC_2828.jpg',
    'https://customer-assets.emergentagent.com/job_pedigree-cats/artifacts/uovr5aif_filhotes3.JPG'
  ];

  useEffect(() => {
    fetchInstagramPhotos();
    
    // Auto-rotate hero images every 4 seconds
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const fetchInstagramPhotos = async () => {
    try {
      const data = await instagramAPI.getFeed();
      if (data.success) {
        // Get first 8 photos (2 rows of 4)
        setInstagramPhotos(data.data.slice(0, 8));
      }
    } catch (error) {
      console.error('Error fetching Instagram feed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
            {/* Text Content */}
            <div className="text-center lg:text-left order-2 lg:order-1">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold text-brown-600 mb-6 tracking-tight">
                {t.hero.title}
              </h1>
              <p className="text-xl sm:text-2xl text-gray-600 mb-8">
                {t.hero.subtitle}
              </p>
              <button
                onClick={() => {
                  navigate('/about');
                  setTimeout(() => window.scrollTo(0, 0), 100);
                }}
                className="inline-block px-8 py-3 bg-brown-900 text-white rounded-full hover:bg-brown-800 transition-all duration-200 text-lg font-medium"
              >
                {t.hero.cta}
              </button>
            </div>

            {/* Featured Image Carousel */}
            <div className="order-1 lg:order-2">
              <div>
                {/* Carousel Images Container */}
                <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                  {heroImages.map((image, index) => (
                    <img
                      loading="lazy" decoding="async" key={index}
                      src={image}
                      alt={`Ocicat ${index + 1}`}
                      className={`w-full h-auto object-cover transition-opacity duration-1000 ${
                        index === currentImageIndex ? 'opacity-100' : 'opacity-0 absolute top-0 left-0'
                      }`}
                    />
                  ))}
                  
                  {/* Carousel Indicators - Inside Image */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                    {heroImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-3 h-3 rounded-full transition-all ${
                          index === currentImageIndex 
                            ? 'bg-white scale-125' 
                            : 'bg-white/50 hover:bg-white/75'
                        }`}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Photo Credit - Outside Image */}
                <p className="text-xs text-gray-500 mt-2 text-right italic">Foto: Carla Durante</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Want an Ocicat */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brown-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            {language === 'pt' ? 'Gostaria de ter um Ocicat?' : 'Would you like to have an Ocicat?'}
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            {language === 'pt' 
              ? 'Descubra como você pode fazer parte da família Ocicat Brasil'
              : 'Discover how you can become part of the Ocicat Brasil family'}
          </p>
          <button
            onClick={() => {
              navigate('/cattery?tab=how');
              setTimeout(() => window.scrollTo(0, 0), 100);
            }}
            className="inline-block px-10 py-4 bg-white text-brown-900 rounded-full hover:bg-gray-100 transition-all duration-200 text-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            {language === 'pt' ? 'Saiba Mais' : 'Learn More'}
          </button>
        </div>
      </section>

      {/* Instagram Gallery Section */}
      {instagramPhotos.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold text-brown-600 mb-2">
                {language === 'pt' ? 'Galeria de Fotos' : 'Photo Gallery'}
              </h2>
              <a 
                href="https://www.instagram.com/ocicat.brasil" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-brown-900 hover:text-brown-700 transition-colors"
              >
                @ocicat.brasil
              </a>
            </div>

            {/* Photo Grid - 2 rows of 4 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {instagramPhotos.map((photo) => (
                <a
                  key={photo.id}
                  href={photo.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square overflow-hidden rounded-lg hover:scale-105 transition-transform duration-300 shadow-md hover:shadow-xl"
                >
                  <img
                    src={photo.media_type === 'VIDEO' ? (photo.thumbnail_url || photo.media_url) : photo.media_url}
                    alt={photo.caption || 'Ocicat'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>

            {/* Ver Todas as Fotos Button */}
            <div className="text-center">
              <button
                onClick={() => navigate('/cats?tab=photos')}
                className="inline-block px-8 py-3 bg-brown-900 text-white rounded-full hover:bg-brown-800 transition-all duration-200 text-lg font-medium"
              >
                {language === 'pt' ? 'Ver Todas as Fotos' : 'View All Photos'}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Hero Image Perth Selvagem */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img
              loading="lazy" decoding="async" src="https://customer-assets.emergentagent.com/job_elegant-ocicat/artifacts/0horxtao_perth-selvaggem.jpeg"
              alt="Perth"
              className="w-full h-auto object-cover"
            />
          </div>
          <p className="text-xs text-gray-500 mt-2 text-right italic">Foto: Carla Durante</p>
        </div>
      </section>

      <SponsorBanner language={language} />
      <Footer />
    </div>
  );
};
